import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StatsSection from "@/components/StatsSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useLang } from "@/context/LanguageContext";
import { ShieldCheck, HeartHandshake, Globe2, Award } from "lucide-react";

export default function AboutPage() {
  const { t } = useLang();

  const values = [
    { icon: ShieldCheck, title: "Trust & Transparency", desc: "Fixed-fee pricing, clear timelines, no hidden costs." },
    { icon: HeartHandshake, title: "White-Glove Service", desc: "A dedicated advisor stays with you from first call to landing." },
    { icon: Globe2, title: "European Expertise", desc: "12+ years navigating Schengen, EU Blue Card, family reunion law." },
    { icon: Award, title: "98% Approval Rate", desc: "Backed by thousands of successful applications across 25 countries." },
  ];

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)]">
        <section className="pt-28 pb-20 bg-radial-gold relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="overline mb-3">{t("nav.about")}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
              We make Europe <span className="gold-text">feel like home.</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed max-w-3xl mx-auto">
              Eazy-Travels is a premium consultancy that has helped over 8,500 individuals, families and companies
              relocate, study, and build new lives across the Schengen Area and beyond. From your first question to
              your first sunrise abroad — we handle every detail.
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.title}
                    className="card-dark p-6 fade-up"
                    style={{ animationDelay: `${i * 60}ms` }}
                    data-testid={`value-card-${i}`}
                  >
                    <div className="w-12 h-12 grid place-items-center rounded-xl bg-gradient-to-br from-[#ffb870]/25 to-[#1e90d5]/20 border border-[var(--brand-orange)]/30 mb-5">
                      <Icon size={22} className="text-[var(--brand-orange-bright)]" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-white mb-2">{v.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-[var(--surface)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden gold-glow">
              <img
                src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1400&q=80"
                alt="Our team"
                className="w-full h-[480px] object-cover"
              />
            </div>
            <div>
              <p className="overline mb-3">Our Story</p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Born from a passion for borderless living.
              </h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Founded in Bucharest in 2013, Eazy-Travels was built on a simple belief: that paperwork should never
                stand between you and your dreams. What started as a two-person office has become a trusted partner to
                students, families, professionals and entrepreneurs across Romania, France, Germany, Italy and the
                wider Schengen area.
              </p>
              <p className="text-white/70 leading-relaxed">
                Today, our multilingual team of advisors, lawyers and relocation specialists handles every facet of
                international mobility — from a first student permit to opening a multinational headquarters.
              </p>
            </div>
          </div>
        </section>

        <StatsSection />
        <TestimonialsSection />
      </main>
      <Footer />
    </>
  );
}
